/**
 * InputFrame.bits layout (brief §5.1). This is shared code: the browser SDK
 * writes these bits, the headless verifier replays them. Change here = change
 * everywhere, including already-recorded run logs — so don't.
 *
 *   bit 0      PRIMARY        tap / drop / link
 *   bit 1      SECONDARY
 *   bit 2      PAUSE_MARKER   set on the tick a visibility pause began
 *   bits 3–4   CHOICE_INDEX   0 = none, 1..3 = the option picked this tick
 *   bits 5–15  reserved, must be zero
 */

export const INPUT_BIT_PRIMARY = 1 << 0;
export const INPUT_BIT_SECONDARY = 1 << 1;
export const INPUT_BIT_PAUSE_MARKER = 1 << 2;

export const CHOICE_INDEX_SHIFT = 3;
export const CHOICE_INDEX_MASK = 0b11 << CHOICE_INDEX_SHIFT;

/** Bits 5–15. Any set bit here makes the frame invalid. */
export const INPUT_RESERVED_MASK = 0xffff & ~(INPUT_BIT_PRIMARY | INPUT_BIT_SECONDARY | INPUT_BIT_PAUSE_MARKER | CHOICE_INDEX_MASK);

/** Read the choice picked this tick: 0 = none, 1..3 = option (1-based). */
export function readChoice(bits: number): number {
  return (bits & CHOICE_INDEX_MASK) >> CHOICE_INDEX_SHIFT;
}

/** Set the choice bits (choice: 0 = none, 1..3 = option picked). */
export function withChoice(bits: number, choice: number): number {
  if (choice < 0 || choice > 3 || !Number.isInteger(choice)) {
    throw new RangeError(`choice must be an integer 0..3, got ${choice}`);
  }
  return (bits & ~CHOICE_INDEX_MASK) | (choice << CHOICE_INDEX_SHIFT);
}

/** True iff the frame uses only defined bits and int16 axes. */
export function isValidFrame(bits: number, axisX: number, axisY: number): boolean {
  const bitsOk = Number.isInteger(bits) && bits >= 0 && (bits & INPUT_RESERVED_MASK) === 0 && bits <= 0xffff;
  const axisOk = (v: number) => Number.isInteger(v) && v >= -32768 && v <= 32767;
  return bitsOk && axisOk(axisX) && axisOk(axisY);
}
